import { Calendar, Clock, Home, Users, CheckCircle, Globe } from 'lucide-react';
import { programFeatures, faqs } from '../mock';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Link } from 'react-router-dom';

const iconMap = {
  Calendar: Calendar,
  Clock: Clock,
  Home: Home,
  Users: Users
};

const Program = () => {
  return (
    <div className="program-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1 className="page-title">NOTRE PROGRAMME</h1>
          <p className="page-subtitle">
            Une immersion anglophone adaptée à vos besoins
          </p>
        </div>
      </section>

      {/* Program Overview */}
      <section className="section-container">
        <div className="program-overview">
          <div className="overview-image">
            <img 
              src="https://images.unsplash.com/photo-1629360021730-3d258452c425?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBsZWFybmluZ3xlbnwwfHx8fDE3NjgyMjA0MzN8MA&ixlib=rb-4.1.0&q=85"
              alt="Apprentissage en famille"
              className="rounded-image"
            />
          </div>
          <div className="overview-content">
            <h2 className="section-title">Notre Offre</h2>
            <p className="section-text">
              Plus enrichissant et formateur qu'un stage en anglais, notre programme propose de pratiquer 
              l'anglais en accueillant un anglophone dans votre famille pour une durée de 1 à 3 mois maximum.
            </p>
            <p className="section-text">
              En échange du logement et des repas pris en famille, l'anglophone propose <strong>15 heures 
              de cours d'anglais, soutien scolaire et conversation par semaine</strong> aux membres de la famille 
              (parents et enfants).
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="program-features">
        <div className="section-container">
          <h2 className="section-title">Les Avantages du Programme</h2>
          <div className="features-grid">
            {programFeatures.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={feature.id} className="program-feature-card">
                  <div className="feature-icon">
                    <IconComponent size={36} />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="included-section">
        <div className="section-container">
          <h2 className="section-title">Ce qui est inclus</h2>
          <div className="included-grid">
            <div className="included-card">
              <CheckCircle className="check-icon" size={24} />
              <h3>Sélection personnalisée</h3>
              <p>Nous choisissons l'anglophone qui correspond à vos attentes et à votre profil familial</p>
            </div>
            <div className="included-card">
              <CheckCircle className="check-icon" size={24} />
              <h3>Mise en relation</h3>
              <p>Accompagnement complet lors de la rencontre et du début du séjour</p>
            </div>
            <div className="included-card">
              <CheckCircle className="check-icon" size={24} />
              <h3>Suivi régulier</h3>
              <p>Points réguliers avec la famille et l'anglophone tout au long du séjour</p>
            </div>
            <div className="included-card">
              <CheckCircle className="check-icon" size={24} />
              <h3>Support continu</h3>
              <p>Équipe disponible pour répondre à vos questions et préoccupations</p>
            </div>
            <div className="included-card">
              <CheckCircle className="check-icon" size={24} />
              <h3>Ateliers communautaires</h3>
              <p>Possibilité pour les anglophones de participer à des interventions dans les écoles locales</p>
            </div>
            <div className="included-card">
              <CheckCircle className="check-icon" size={24} />
              <h3>Immersion culturelle</h3>
              <p>Découverte mutuelle des cultures française et anglophone</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="section-container">
          <h2 className="section-title">Le Processus en Détail</h2>
          <div className="process-timeline">
            <div className="timeline-item">
              <div className="timeline-marker">1</div>
              <div className="timeline-content">
                <h3>Premier Contact</h3>
                <p>
                  Vous nous contactez via notre formulaire ou par téléphone. Nous échangeons sur vos attentes, 
                  vos besoins spécifiques et le profil de votre famille.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">2</div>
              <div className="timeline-content">
                <h3>Sélection de l'Anglophone</h3>
                <p>
                  Notre équipe analyse votre demande et sélectionne l'anglophone le plus adapté à votre famille. 
                  Nous prenons en compte les personnalités, les intérêts et les objectifs d'apprentissage.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">3</div>
              <div className="timeline-content">
                <h3>Rencontre et Mise en Relation</h3>
                <p>
                  Organisation d'une rencontre entre votre famille et l'anglophone pour faire connaissance. 
                  Nous facilitons cette première prise de contact.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">4</div>
              <div className="timeline-content">
                <h3>Début du Séjour</h3>
                <p>
                  L'anglophone intègre votre foyer et commence les cours d'anglais. Un planning est établi 
                  ensemble pour les 15 heures hebdomadaires.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">5</div>
              <div className="timeline-content">
                <h3>Suivi et Accompagnement</h3>
                <p>
                  Tout au long du séjour, notre équipe reste en contact avec vous et l'anglophone pour 
                  s'assurer que tout se passe bien et ajuster si nécessaire.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-container">
          <h2 className="section-title">Questions Fréquentes</h2>
          <Accordion type="single" collapsible className="faq-accordion">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger>{faq.question}</AccordionTrigger>
                <AccordionContent>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="program-cta">
        <div className="program-cta-content">
          <Globe size={48} className="cta-icon" />
          <h2>Intéressé par notre programme ?</h2>
          <p>Contactez-nous pour discuter de vos besoins et commencer l'aventure</p>
          <Link to="/contact">
            <Button className="btn-cta">Nous contacter</Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Program;